// src/pages/Contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../Firebase/Config';
import { CustomerProfile, createCustomerProfile, getCustomerProfile } from '../Services/customerService';

interface AuthContextType {
  currentUser: User | null;
  customerProfile: CustomerProfile | null;
  loading: boolean;
  signup: (name: string, email: string, password: string, phone?: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (uid: string) => {
    const profile = await getCustomerProfile(uid);
    setCustomerProfile(profile);
  };

  const refreshProfile = async () => {
    if (currentUser?.uid) await fetchProfile(currentUser.uid);
  };

  const signup = async (name: string, email: string, password: string, phone?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName: name });
    await createCustomerProfile(userCredential.user.uid, { name, email, phone });
    await fetchProfile(userCredential.user.uid);
    return userCredential;
  };

  // Blocks sign-in for a customer an admin has disabled — signs them straight
  // back out so no session is kept, mirroring the admin app's staff-login guard.
  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getCustomerProfile(userCredential.user.uid);

    if (profile && !profile.isActive) {
      await signOut(auth);
      throw new Error('Your account has been disabled. Please contact support.');
    }

    setCustomerProfile(profile);
    return userCredential;
  };

  const logout = async () => {
    await signOut(auth);
    setCustomerProfile(null);
  };

  // Continue URL points back to the client's own login page — Firebase's
  // default-hosted reset-password page handles the actual reset form itself
  // (mirrors the admin app's Forgotpasswordfirebase.tsx, same Auth pool).
  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email, {
      url: window.location.origin + '/login',
      handleCodeInApp: false,
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user?.uid) {
        const profile = await getCustomerProfile(user.uid);
        if (profile && !profile.isActive) {
          // Account was disabled after this session started — end it now.
          await signOut(auth);
          setCustomerProfile(null);
        } else {
          setCustomerProfile(profile);
        }
      } else {
        setCustomerProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    customerProfile,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
